import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import { PedidosVentaApi } from "../../api/PedidosVenta/PedidosVentaApi";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pygHeroShellSx,
  pygToolbarFieldSx,
  pygToolbarControlSx,
} from "../PyGAnalitico/PygAnaliticoPageHeader";
import RankingVentasFoto from "./RankingVentasFoto";
import { pageMobileCardSx } from "../../styles/pageModernStyles";

const EMPRESA_OPTIONS = [
  { value: "stage", label: "Stage", color: "primary" },
  { value: "erna", label: "ERNA", color: "secondary" },
  { value: "fusion", label: "Fusión", color: "info" },
];

const SEASON_COLORS = [
  "#0ea5e9",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#6366f1",
  "#14b8a6",
  "#ec4899",
];

const formatImporte = (value) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatCantidad = (value) =>
  new Intl.NumberFormat("es-ES", {
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const RankBadge = ({ rank }) => {
  const medals = {
    1: {
      bg: "linear-gradient(145deg, #ffe566 0%, #ffb800 100%)",
      color: "#5c4500",
      shadow: "0 2px 8px rgba(255,184,0,0.45)",
    },
    2: {
      bg: "linear-gradient(145deg, #f0f0f0 0%, #b8b8b8 100%)",
      color: "#3d3d3d",
      shadow: "0 2px 8px rgba(0,0,0,0.12)",
    },
    3: {
      bg: "linear-gradient(145deg, #e8b88a 0%, #c87941 100%)",
      color: "#4a2e12",
      shadow: "0 2px 8px rgba(200,121,65,0.35)",
    },
  };
  const medal = medals[rank];

  if (medal) {
    return (
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: medal.bg,
          color: medal.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 900,
          fontSize: "0.8rem",
          boxShadow: medal.shadow,
        }}
      >
        {rank}
      </Box>
    );
  }

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 700, width: 30, textAlign: "center", display: "block" }}
    >
      {rank}
    </Typography>
  );
};

const GarmentBar = ({ value, max, rank, accent, theme, fullWidth = false }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const barColor =
    rank === 1
      ? theme.palette.warning.main
      : rank === 2
        ? alpha(theme.palette.text.secondary, 0.55)
        : rank === 3
          ? "#c87941"
          : accent;

  return (
    <Stack
      alignItems={fullWidth ? "stretch" : "flex-end"}
      spacing={0.35}
      sx={{ width: "100%" }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 800,
          fontVariantNumeric: "tabular-nums",
          textAlign: fullWidth ? "left" : "right",
        }}
      >
        {formatCantidad(value)}
      </Typography>
      <Box
        sx={{
          width: "100%",
          maxWidth: fullWidth ? "100%" : 96,
          height: 5,
          borderRadius: 1,
          bgcolor: alpha(barColor, 0.12),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 1,
            bgcolor: barColor,
            transition: "width 0.35s ease",
          }}
        />
      </Box>
    </Stack>
  );
};

const RankingVentasPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;
  const isDark = theme.palette.mode === "dark";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(dayjs().year());
  const [empresa, setEmpresa] = useState("fusion");
  const [account, setAccount] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setAccount("");
  }, [year, empresa]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await PedidosVentaApi.getRankingVentas(empresa, year);
        const normalized = (data || []).map((row) => ({
          CODART: row.CODART ?? row.codart ?? "",
          ARTALIAS: row.ARTALIAS ?? row.artalias ?? row.CODART ?? "",
          Temporada: row.Temporada ?? row.temporada ?? row.CAR1 ?? "",
          CLIPRO: row.CLIPRO ?? row.clipro ?? "",
          JefeCliente:
            row.JefeCliente ??
            row.jefeCliente ??
            row.ResponsableVentas ??
            row.responsableVentas ??
            row.REPALIAS ??
            row.repalias ??
            "",
          REPALIAS: row.REPALIAS ?? row.repalias ?? "",
          PrendasVendidas: Number(
            row.PrendasVendidas ?? row.prendasVendidas ?? 0,
          ),
          PrecioUnidad: Number(row.PrecioUnidad ?? row.precioUnidad ?? 0),
          IdMuestra: row.IdMuestra ?? row.idMuestra ?? null,
          IdProducto: row.IdProducto ?? row.idProducto ?? null,
          NumeroPresupuesto:
            row.NumeroPresupuesto ?? row.numeroPresupuesto ?? null,
        }));
        setRows(normalized);
      } catch (err) {
        console.error("❌ Error cargando ranking de ventas:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year, empresa]);

  const accountOptions = useMemo(() => {
    const options = new Set();
    rows.forEach((row) => {
      const rep = String(row.REPALIAS || "").trim();
      if (rep) options.add(rep);
    });
    return [...options].sort((a, b) => a.localeCompare(b, "es"));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = rows;

    if (account) {
      result = result.filter(
        (row) => String(row.REPALIAS || "").trim() === account,
      );
    }

    const query = search.toLowerCase().trim();
    if (!query) return result;

    return result.filter((row) =>
      [
        row.CODART,
        row.ARTALIAS,
        row.Temporada,
        row.CLIPRO,
        row.JefeCliente,
        row.REPALIAS,
      ].some((field) =>
        String(field || "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [rows, search, account]);

  const stats = useMemo(() => {
    const totalPrendas = filteredRows.reduce(
      (sum, row) => sum + (row.PrendasVendidas || 0),
      0,
    );
    const clients = new Set(
      filteredRows.map((r) => String(r.CLIPRO || "").trim()).filter(Boolean),
    );
    const maxPrendas = filteredRows[0]?.PrendasVendidas || 0;
    return { totalPrendas, clientCount: clients.size, maxPrendas };
  }, [filteredRows]);

  const topArticlesChart = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((row) => {
      const key = row.ARTALIAS || row.CODART || "—";
      map.set(key, (map.get(key) || 0) + (row.PrendasVendidas || 0));
    });
    return [...map.entries()]
      .map(([name, prendas]) => ({
        name: name.length > 22 ? `${name.slice(0, 20)}…` : name,
        fullName: name,
        prendas,
      }))
      .sort((a, b) => b.prendas - a.prendas)
      .slice(0, 10)
      .reverse();
  }, [filteredRows]);

  const seasonChart = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((row) => {
      const season =
        row.Temporada && row.Temporada !== "—"
          ? row.Temporada
          : t("rankingVentas.noSeason");
      map.set(season, (map.get(season) || 0) + (row.PrendasVendidas || 0));
    });
    return [...map.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredRows, t]);

  const empresaMeta = EMPRESA_OPTIONS.find((o) => o.value === empresa);
  const toolbarFieldSx = { ...pygToolbarFieldSx(theme), ...pygToolbarControlSx };

  const getPodiumAccent = (rank) =>
    rank === 1
      ? theme.palette.warning.main
      : rank === 2
        ? theme.palette.grey[500]
        : rank === 3
          ? "#c87941"
          : accent;

  const renderMobileRow = (row, index) => {
    const rank = index + 1;
    const podiumAccent = getPodiumAccent(rank);

    return (
      <Box
        key={`${row.CODART}-${row.Temporada}-${row.CLIPRO}-${index}`}
        sx={{
          ...pageMobileCardSx(theme, { accentColor: podiumAccent }),
          ...(rank <= 3
            ? { bgcolor: alpha(podiumAccent, isDark ? 0.1 : 0.06) }
            : {}),
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Stack alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            <RankBadge rank={rank} />
            <RankingVentasFoto
              idMuestra={row.IdMuestra}
              idProducto={row.IdProducto}
              numeroPresupuesto={row.NumeroPresupuesto}
              label={row.ARTALIAS || row.CODART}
            />
          </Stack>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
              {row.ARTALIAS || row.CODART}
            </Typography>
            {row.CODART && row.ARTALIAS !== row.CODART ? (
              <Typography variant="caption" color="text.secondary">
                {row.CODART}
              </Typography>
            ) : null}

            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 0.75 }}
            >
              {row.Temporada && row.Temporada !== "—" ? (
                <Chip
                  label={row.Temporada}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
                />
              ) : null}
              <Chip
                label={formatImporte(row.PrecioUnidad)}
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: "0.68rem",
                  bgcolor: alpha(accent, 0.08),
                }}
              />
            </Stack>

            {row.CLIPRO ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.75, lineHeight: 1.35 }}
              >
                {row.CLIPRO}
              </Typography>
            ) : null}

            {row.JefeCliente ? (
              <Chip
                label={row.JefeCliente}
                size="small"
                sx={{
                  mt: 0.5,
                  height: 22,
                  maxWidth: "100%",
                  fontWeight: 600,
                  fontSize: "0.68rem",
                  bgcolor: alpha(accent, 0.08),
                }}
              />
            ) : null}

            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, display: "block", mb: 0.35 }}
              >
                {t("rankingVentas.garmentsSold")}
              </Typography>
              <GarmentBar
                value={row.PrendasVendidas}
                max={stats.maxPrendas}
                rank={rank}
                accent={accent}
                theme={theme}
                fullWidth
              />
            </Box>
          </Box>
        </Stack>
      </Box>
    );
  };

  const tableHeadSx = {
    fontWeight: 800,
    fontSize: "0.75rem",
    letterSpacing: 0.3,
    textTransform: "uppercase",
    bgcolor: isDark ? theme.palette.grey[900] : theme.palette.grey[50],
    borderBottom: "2px solid",
    borderColor: alpha(accent, 0.22),
    py: 1.1,
    zIndex: 2,
    boxShadow: isDark
      ? "0 1px 0 rgba(255,255,255,0.06)"
      : "0 1px 0 rgba(0,0,0,0.04)",
  };

  const chartPanelSx = {
    p: 1.25,
    borderRadius: 2.5,
    border: "1px solid",
    borderColor: alpha(theme.palette.divider, 0.85),
    height: { xs: 280, lg: 220 },
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: isDark
      ? "0 4px 20px rgba(0,0,0,0.2)"
      : "0 6px 24px rgba(15,23,42,0.05)",
  };

  return (
    <Box
      sx={{
        ...contentMargin,
        p: { xs: 1.25, sm: 2 },
        pb: { xs: 10, sm: 2 },
        boxSizing: "border-box",
        ...(isMobile
          ? { minHeight: "100vh" }
          : {
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }),
      }}
    >
      <Box sx={pygHeroShellSx(theme, { sticky: !isMobile })}>
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ lg: "flex-start" }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(accent, 0.16),
                  color: theme.palette.primary.dark,
                  border: "1px solid",
                  borderColor: alpha(accent, 0.28),
                }}
              >
                <LeaderboardOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: 1.1 }}
                >
                  {t("rankingVentas.overline")}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: { xs: "1.35rem", md: "1.75rem" } }}
                >
                  {t("sidebar.rankingVentas")}{" "}
                  <Box component="span" sx={{ color: accent }}>
                    · {year}
                  </Box>
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.75 }}
                >
                  <Chip
                    size="small"
                    label={t("rankingVentas.rows", { count: filteredRows.length })}
                    sx={{ fontWeight: 700, bgcolor: alpha(accent, 0.08) }}
                  />
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<EmojiEventsOutlinedIcon sx={{ fontSize: "1rem !important" }} />}
                    label={t("rankingVentas.totalGarments", {
                      count: formatCantidad(stats.totalPrendas),
                    })}
                    sx={{ fontWeight: 700 }}
                  />
                  {empresaMeta ? (
                    <Chip
                      size="small"
                      color={empresaMeta.color}
                      label={empresaMeta.label}
                      sx={{ fontWeight: 700 }}
                    />
                  ) : null}
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ width: { xs: "100%", lg: "auto" }, minWidth: { lg: 520 } }}
            >
              <TextField
                select
                size="small"
                label={t("rankingVentas.year")}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                sx={{ minWidth: { xs: "100%", sm: 108 }, ...toolbarFieldSx }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonthOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              >
                {Array.from({ length: 8 }, (_, i) => dayjs().year() - i).map(
                  (y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ),
                )}
              </TextField>

              <TextField
                select
                size="small"
                label={t("rankingVentas.company")}
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 128 }, ...toolbarFieldSx }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              >
                {EMPRESA_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label={t("rankingVentas.account")}
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 168 }, ...toolbarFieldSx }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="">{t("rankingVentas.allAccounts")}</MenuItem>
                {accountOptions.map((rep) => (
                  <MenuItem key={rep} value={rep}>
                    {rep}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                placeholder={t("rankingVentas.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 220 }, flex: 1, ...toolbarFieldSx }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </Box>

      {!loading && filteredRows.length > 0 ? (
        <Grid container spacing={1.25} sx={{ mb: 1.25, flexShrink: 0 }}>
          <Grid item xs={12} lg={7}>
            <Paper elevation={0} sx={chartPanelSx}>
              <Typography
                variant="caption"
                fontWeight={800}
                color="text.secondary"
                sx={{ letterSpacing: 0.5, flexShrink: 0 }}
              >
                {t("rankingVentas.chartTopArticles")}
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0, mt: 0.5 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topArticlesChart}
                    layout="vertical"
                    margin={{ top: 4, right: 8, left: 4, bottom: 0 }}
                  >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    stroke={alpha(theme.palette.divider, 0.8)}
                  />
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={108}
                    tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(v) => [formatCantidad(v), t("rankingVentas.garmentsSold")]}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.fullName ?? ""
                    }
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  />
                  <Bar dataKey="prendas" radius={[0, 4, 4, 0]} maxBarSize={14}>
                    {topArticlesChart.map((entry, i) => (
                      <Cell
                        key={entry.fullName}
                        fill={alpha(
                          SEASON_COLORS[i % SEASON_COLORS.length],
                          isDark ? 0.85 : 0.78,
                        )}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Paper
              elevation={0}
              sx={{
                ...chartPanelSx,
                height: { xs: "auto", lg: 220 },
                minHeight: { xs: 300, lg: 220 },
              }}
            >
              <Typography
                variant="caption"
                fontWeight={800}
                color="text.secondary"
                sx={{ letterSpacing: 0.5, flexShrink: 0 }}
              >
                {t("rankingVentas.chartBySeason")}
              </Typography>
              <Box sx={{ flex: 1, minHeight: { xs: 180, lg: 0 }, mt: 0.25 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={seasonChart}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="48%"
                      innerRadius="38%"
                      outerRadius="58%"
                      paddingAngle={2}
                    >
                    {seasonChart.map((entry, i) => (
                      <Cell
                        key={entry.name}
                        fill={SEASON_COLORS[i % SEASON_COLORS.length]}
                        stroke={theme.palette.background.paper}
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => formatCantidad(v)}
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                useFlexGap
                flexWrap="wrap"
                sx={{ flexShrink: 0, pt: 0.5 }}
              >
                {seasonChart.map((s, i) => (
                  <Chip
                    key={s.name}
                    size="small"
                    label={`${s.name} · ${formatCantidad(s.value)}`}
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      maxWidth: "100%",
                      bgcolor: alpha(SEASON_COLORS[i % SEASON_COLORS.length], 0.14),
                    }}
                  />
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          ...(isMobile ? { mt: 1.25 } : { flex: 1, minHeight: 0 }),
          display: "flex",
          flexDirection: "column",
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.85),
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: isDark
            ? "0 8px 28px rgba(0,0,0,0.22)"
            : "0 10px 36px rgba(15,23,42,0.06)",
          position: "relative",
        }}
      >
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {t("rankingVentas.loading")}
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ overflow: "auto", p: 1, WebkitOverflowScrolling: "touch" }}>
            {filteredRows.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <LeaderboardOutlinedIcon
                  sx={{ fontSize: 40, color: alpha(accent, 0.35), mb: 1 }}
                />
                <Typography color="text.secondary" fontWeight={600}>
                  {t("rankingVentas.empty")}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {filteredRows.map(renderMobileRow)}
              </Stack>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table
              stickyHeader
              size="small"
              sx={{ tableLayout: "fixed", minWidth: 1040 }}
            >
              <TableHead
                sx={{
                  "& .MuiTableCell-head": {
                    bgcolor: isDark ? theme.palette.grey[900] : theme.palette.grey[50],
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ ...tableHeadSx, width: 44, px: 1 }}>#</TableCell>
                  <TableCell sx={{ ...tableHeadSx, width: 56, px: 1 }}>
                    {t("rankingVentas.photo")}
                  </TableCell>
                  <TableCell sx={{ ...tableHeadSx, width: "16%" }}>
                    {t("rankingVentas.article")}
                  </TableCell>
                  <TableCell sx={{ ...tableHeadSx, width: "9%" }}>
                    {t("rankingVentas.season")}
                  </TableCell>
                  <TableCell sx={{ ...tableHeadSx, width: "20%" }}>
                    {t("rankingVentas.client")}
                  </TableCell>
                  <TableCell sx={{ ...tableHeadSx, width: "12%" }}>
                    {t("rankingVentas.clientOwner")}
                  </TableCell>
                  <TableCell align="right" sx={{ ...tableHeadSx, width: "9%" }}>
                    {t("rankingVentas.price")}
                  </TableCell>
                  <TableCell align="right" sx={{ ...tableHeadSx, width: "11%" }}>
                    {t("rankingVentas.garmentsSold")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <LeaderboardOutlinedIcon
                        sx={{ fontSize: 40, color: alpha(accent, 0.35), mb: 1 }}
                      />
                      <Typography color="text.secondary" fontWeight={600}>
                        {t("rankingVentas.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row, index) => {
                    const rank = index + 1;
                    const isPodium = rank <= 3;

                    return (
                      <TableRow
                        key={`${row.CODART}-${row.Temporada}-${row.CLIPRO}-${index}`}
                        hover
                        sx={{
                          "& td": { py: 0.85, verticalAlign: "middle" },
                          bgcolor: isPodium
                            ? alpha(
                                rank === 1
                                  ? theme.palette.warning.main
                                  : rank === 2
                                    ? theme.palette.grey[500]
                                    : "#c87941",
                                isDark ? 0.08 : 0.05,
                              )
                            : undefined,
                        }}
                      >
                        <TableCell sx={{ px: 1 }}>
                          <RankBadge rank={rank} />
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                          <RankingVentasFoto
                            idMuestra={row.IdMuestra}
                            idProducto={row.IdProducto}
                            numeroPresupuesto={row.NumeroPresupuesto}
                            label={row.ARTALIAS || row.CODART}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {row.ARTALIAS || row.CODART}
                          </Typography>
                          {row.CODART && row.ARTALIAS !== row.CODART ? (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {row.CODART}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {row.Temporada && row.Temporada !== "—" ? (
                            <Chip
                              label={row.Temporada}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontWeight: 700, fontSize: "0.7rem" }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={row.CLIPRO}
                        >
                          {row.CLIPRO}
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={row.JefeCliente}
                        >
                          {row.JefeCliente ? (
                            <Chip
                              label={row.JefeCliente}
                              size="small"
                              sx={{
                                height: 22,
                                maxWidth: "100%",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                bgcolor: alpha(accent, 0.08),
                              }}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="body2"
                            sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
                          >
                            {formatImporte(row.PrecioUnidad)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <GarmentBar
                            value={row.PrendasVendidas}
                            max={stats.maxPrendas}
                            rank={rank}
                            accent={accent}
                            theme={theme}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default RankingVentasPage;
